import { useCallback, useEffect, useState } from "react";
import { Radio as RadioIcon, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { StationCard } from "@/components/radio/StationCard";
import { AddStationDialog } from "@/components/radio/AddStationDialog";
import { Discover } from "@/components/radio/Discover";
import { LibraryRadio } from "@/components/radio/LibraryRadio";
import { Schedules } from "@/components/radio/Schedules";
import { History } from "@/components/radio/History";
import { api, formatApiError } from "@/lib/api";

const MyStations = ({ stations, setStations, onAdd, onDiscover }) => {
  const favorite = async (s) => {
    const { data } = await api.patch(`/radio/stations/${s.id}`, { favorite: !s.favorite });
    setStations((xs) => xs.map((x) => (x.id === s.id ? data : x)).sort((a, b) => (b.favorite - a.favorite) || a.name.localeCompare(b.name)));
  };
  const rename = async (s) => {
    const name = window.prompt("Rename station", s.name);
    if (!name?.trim()) return;
    try { const { data } = await api.patch(`/radio/stations/${s.id}`, { name }); setStations((xs) => xs.map((x) => (x.id === s.id ? data : x))); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}? Its schedule slots are removed too.`)) return;
    await api.delete(`/radio/stations/${s.id}`);
    setStations((xs) => xs.filter((x) => x.id !== s.id));
    window.dispatchEvent(new Event("radio-schedules-changed"));
  };
  if (!stations.length) {
    return <EmptyState icon={RadioIcon} title="No stations yet" text="Find live stations from around the world, or paste your own stream link."
      action={<div className="flex gap-3">
        <button onClick={onDiscover} data-testid="stations-empty-discover" className="rounded-full bg-zinc-900 px-5 py-2 text-sm text-white">Discover stations</button>
        <button onClick={onAdd} data-testid="stations-empty-add" className="rounded-full border border-zinc-300 px-5 py-2 text-sm">Add stream link</button>
      </div>} />;
  }
  return (
    <div className="grid gap-3 lg:grid-cols-2" data-testid="my-stations-grid">
      {stations.map((s) => <StationCard key={s.id} s={s} onFavorite={favorite} onRename={rename} onDelete={remove} />)}
    </div>
  );
};

export default function Radio() {
  const [tab, setTab] = useState("stations");
  const [stations, setStations] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.get("/radio/stations").then((r) => setStations(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const added = (s) => setStations((xs) => [...(xs || []), s]);

  return (
    <div>
      <PageHeader eyebrow="Live & endless" title="Radio" testid="radio-title">
        <button onClick={() => setAdding(true)} data-testid="radio-add-stream-button" className="flex h-11 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900"><Plus size={15} />Add stream link</button>
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-8 h-11 rounded-full bg-zinc-100 p-1" data-testid="radio-tabs">
          <TabsTrigger value="stations" className="rounded-full px-5" data-testid="radio-tab-stations">My stations{stations?.length ? <span className="ml-1.5 font-mono text-[10px] text-zinc-400">{stations.length}</span> : null}</TabsTrigger>
          <TabsTrigger value="discover" className="rounded-full px-5" data-testid="radio-tab-discover">Discover</TabsTrigger>
          <TabsTrigger value="library" className="rounded-full px-5" data-testid="radio-tab-library">Song & artist radio</TabsTrigger>
          <TabsTrigger value="schedule" className="rounded-full px-5" data-testid="radio-tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="history" className="rounded-full px-5" data-testid="radio-tab-history">Song history</TabsTrigger>
        </TabsList>
        {!stations ? <Loader2 className="animate-spin text-zinc-400" /> : (
          <>
            <TabsContent value="stations"><MyStations stations={stations} setStations={setStations} onAdd={() => setAdding(true)} onDiscover={() => setTab("discover")} /></TabsContent>
            <TabsContent value="discover"><Discover onSaved={added} /></TabsContent>
            <TabsContent value="library"><LibraryRadio /></TabsContent>
            <TabsContent value="schedule"><Schedules stations={stations} /></TabsContent>
            <TabsContent value="history"><History /></TabsContent>
          </>
        )}
      </Tabs>
      <AddStationDialog open={adding} onOpenChange={setAdding} onAdded={added} />
    </div>
  );
}
